/**
 * Repository Security Page
 *
 * Security overview, vulnerabilities, SBOM, and scan history
 */

import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Shield } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@forgenetwork/ui';
import {
  SecurityDashboard,
  VulnerabilityList,
  SBOMViewer,
} from '@/components/security';
import {
  useSecurityScore,
  useSecurityScans,
  useVulnerabilities,
  useSBOM,
} from '@/hooks/useSecurity';

export const Route = createFileRoute('/_authenticated/repositories/$repoId/security')({
  component: RepositorySecurityPage,
});

function RepositorySecurityPage(): React.JSX.Element {
  const { repoId } = Route.useParams();
  const navigate = useNavigate();

  // Fetch all security data
  const { data: scoreData } = useSecurityScore(repoId);
  const { data: scansData } = useSecurityScans(repoId);
  const { data: vulnerabilities } = useVulnerabilities(repoId);
  const { data: sbomData } = useSBOM(repoId);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate({ to: '/repositories/$repoId', params: { repoId } })
            }
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-7 w-7 text-primary" />
              <h1 className="text-3xl font-bold tracking-tight">
                Security Analysis
              </h1>
            </div>
            <p className="text-muted-foreground">
              Vulnerability scanning, dependency analysis, and SBOM generation
            </p>
          </div>
        </div>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>About Security Scanning</CardTitle>
            <CardDescription>
              Security scans analyze your dependencies for known vulnerabilities
              (CVEs), check for outdated packages, and generate a Software Bill
              of Materials (SBOM) documenting all components in your project.
              Run regular scans to stay informed about security risks.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="vulnerabilities">
              Vulnerabilities
              {scoreData?.breakdown.vulnerabilities !== undefined &&
                scoreData.breakdown.vulnerabilities > 0 && (
                  <span className="ml-2 bg-destructive text-destructive-foreground rounded-full px-2 py-0.5 text-xs font-semibold">
                    {scoreData.breakdown.vulnerabilities}
                  </span>
                )}
            </TabsTrigger>
            <TabsTrigger value="sbom">SBOM</TabsTrigger>
            <TabsTrigger value="scans">
              Scans
              {scansData?.scans && (
                <span className="ml-2 text-xs text-muted-foreground">
                  ({scansData.scans.length})
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <SecurityDashboard repositoryId={repoId} />
          </TabsContent>

          {/* Vulnerabilities Tab */}
          <TabsContent value="vulnerabilities" className="space-y-6">
            {vulnerabilities && vulnerabilities.length > 0 ? (
              <VulnerabilityList vulnerabilities={vulnerabilities} />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Shield className="h-16 w-16 text-green-600 mb-4" />
                    <h2 className="text-lg font-semibold mb-2">
                      No Vulnerabilities Found
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Your repository has no known security vulnerabilities
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SBOM Tab */}
          <TabsContent value="sbom" className="space-y-6">
            {sbomData?.sbom ? (
              <SBOMViewer sbom={sbomData.sbom} />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Shield className="h-16 w-16 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                      No SBOM Available
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Run a security scan to generate an SBOM
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Scans Tab */}
          <TabsContent value="scans" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Scan History</CardTitle>
                <CardDescription>
                  View all security scans that have been run for this repository
                </CardDescription>
              </CardHeader>
              <CardContent>
                {scansData?.scans && scansData.scans.length > 0 ? (
                  <div className="space-y-2">
                    {scansData.scans.map((scan) => (
                      <div
                        key={scan.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <div className="font-medium text-sm">
                            Scan {scan.id}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {scan.status === 'completed' && scan.completedAt
                              ? new Date(scan.completedAt).toLocaleString()
                              : scan.startedAt
                              ? `Started ${new Date(
                                  scan.startedAt
                                ).toLocaleString()}`
                              : `Created ${new Date(
                                  scan.createdAt
                                ).toLocaleString()}`}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {scan.status === 'completed' && scan.score !== undefined && (
                            <div className="text-right">
                              <div className="text-sm font-semibold">
                                Score: {scan.score}
                              </div>
                              {scan.grade && (
                                <div className="text-xs text-muted-foreground">
                                  Grade: {scan.grade}
                                </div>
                              )}
                            </div>
                          )}
                          <div
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              scan.status === 'completed'
                                ? 'bg-green-100 text-green-800'
                                : scan.status === 'running'
                                ? 'bg-blue-100 text-blue-800'
                                : scan.status === 'failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {(scan.status || 'pending').toUpperCase()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No scans have been run yet
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
